import React from "react";
import { Outlet } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Navigate } from "react-router-dom";
import { Loader2, ShieldX } from "lucide-react";

/**
 * Props del componente ProtectedRouteWrapper
 */
interface ProtectedRouteWrapperProps {
  allowedRoles: string[];
  children?: React.ReactNode;
}

/**
 * @deprecated Usa ProtectedRoute en su lugar para mayor consistencia.
 * Este componente se mantiene para compatibilidad con código legacy.
 *
 * Wrapper para proteger rutas que requieren autenticación y roles específicos
 *
 * Esta versión funciona con rutas anidadas y puede aceptar children
 * o usar Outlet para renderizar rutas hijas.
 *
 * **Recomendación**: Migra a `ProtectedRoute` que ofrece mejor UI/UX
 * y es el componente estándar del proyecto.
 */
const ProtectedRouteWrapper: React.FC<ProtectedRouteWrapperProps> = ({
  allowedRoles,
  children,
}) => {
  const { isAuthenticated, isLoading, user, hasAnyRole } = useAuth();

  // Mostrar loading mientras verifica el estado de autenticación
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Verificando autorización...</p>
        </div>
      </div>
    );
  }

  // Si no está autenticado, redirigir a login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Verificar si el usuario tiene los roles necesarios
  const hasRequiredRoles = hasAnyRole(allowedRoles);

  // Si no tiene los roles necesarios, mostrar página de acceso denegado
  if (!hasRequiredRoles) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <ShieldX className="h-16 w-16 mx-auto text-destructive mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Acceso Denegado
            </h1>
            <p className="text-muted-foreground">
              No tienes permisos para acceder a esta página.
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              <strong>Tu rol actual:</strong> {user?.roles?.join(", ")}
            </p>
            <p className="text-sm text-muted-foreground">
              <strong>Roles requeridos:</strong> {allowedRoles.join(", ")}
            </p>
          </div>

          <div className="mt-6 space-y-2">
            <button
              onClick={() => (window.location.href = "/")}
              className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
            >
              Ir al Inicio
            </button>
            <button
              onClick={() => window.history.back()}
              className="w-full bg-secondary text-secondary-foreground px-4 py-2 rounded-md hover:bg-secondary/90 transition-colors"
            >
              Volver Atrás
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Si tiene los roles necesarios, renderizar el contenido
  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRouteWrapper;
